/**
 * @module features/devices/components/FirmwareTimeline
 * @description Vertical timeline of firmware / OS upgrades for a device.
 */
import * as React from "react";
import { History } from "lucide-react";
import { FirmwareHistoryItem } from "../types";
import { formatDateTime, truncateMiddle } from "../utils/format";

interface FirmwareTimelineProps {
  entries: FirmwareHistoryItem[];
}

export function FirmwareTimeline({ entries }: FirmwareTimelineProps) {
  if (entries.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
        <History className="h-4 w-4 shrink-0" aria-hidden="true" />
        No firmware changes recorded yet.
      </div>
    );
  }

  return (
    <ol className="relative space-y-4 border-l border-border/80 pl-4 ml-1.5">
      {entries.map((entry) => (
        <li key={entry.id} className="relative">
          <span
            aria-hidden="true"
            className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-foreground ring-4 ring-card"
          />
          <p className="text-xs font-semibold text-foreground">
            <span className="font-mono">{entry.previous_os_version}</span>
            <span aria-label="upgraded to"> → </span>
            <span className="font-mono">{entry.new_os_version}</span>
          </p>
          <p
            className="text-[11px] font-mono text-muted-foreground break-all"
            title={`${entry.previous_os_hash} → ${entry.new_os_hash}`}
          >
            {truncateMiddle(entry.previous_os_hash)} → {truncateMiddle(entry.new_os_hash)}
          </p>
          {entry.notes && <p className="mt-0.5 text-xs text-foreground/80">{entry.notes}</p>}
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {formatDateTime(entry.created_at)}
            {entry.created_by_name ? ` · ${entry.created_by_name}` : ""}
          </p>
        </li>
      ))}
    </ol>
  );
}
